import React from 'react';
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MenuOpen as MenuOpenIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import CopyIconButton from '../../../shared/components/CopyIconButton';
import { SHEET_ID } from '../hooks/sheetFocus';
import TapTooltip from '../../../shared/components/TapTooltip';
import UserMenu from '../../user/components/UserMenu';
import EscalationControl from '../../customer/components/EscalationControl';
import CustomerSwitcher from '../../customer/components/CustomerSwitcher';
import type { Customer, Investigation } from '../../../types';
import type { ConnectionState } from '../../../types/frontend';
import { viewportGutterMaxWidth } from '../../../shared/constants/layout';

interface InvestigationHeaderProps {
  selectedCustomer: Customer;
  connectionState: ConnectionState;
  leftSidebarOpen: boolean;
  rightSidebarOpen: boolean;
  onToggleLeftSidebar: () => void;
  onToggleRightSidebar: () => void;
  onManageAccounts: () => void;
  onCustomerUpdated: () => void;
  onInvestigationCreated: (investigation: Investigation, initialMessage?: string) => void;
  onEffectiveLevelChange?: (effectiveLevel: string, ceiling: string) => void;
  /** A compact sheet is open: the bar is out of reach, so nothing it opens can sit over the sheet. */
  inert?: boolean;
}

const CONNECTION_TOOLTIP: Record<ConnectionState, string> = {
  connected: 'Chat Connected',
  connecting: 'Chat Connecting...',
  reconnecting: 'Chat Reconnecting... (automatic retry in progress)',
  disconnected: 'Chat Disconnected (will reconnect automatically)',
};

const CONNECTION_COLOR: Record<ConnectionState, string> = {
  connected: 'success.main',
  connecting: 'warning.main',
  reconnecting: 'warning.main',
  disconnected: 'error.main',
};

const CustomerIdsTooltip: React.FC<{ customer: Customer }> = ({ customer }) => (
  <TapTooltip
    title={
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: { xs: 0, sm: '160px' } }}>
            Customer ID:
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.85em', wordBreak: 'break-all' }}>
            {customer.id}
          </Typography>
          <CopyIconButton text={customer.id} iconSize={14} sx={{ p: 0.5, ml: 0.5 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: { xs: 0, sm: '160px' } }}>
            Operations Account ID:
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.85em', wordBreak: 'break-all' }}>
            {customer.operations_account_id}
          </Typography>
          <CopyIconButton text={customer.operations_account_id} iconSize={14} sx={{ p: 0.5, ml: 0.5 }} />
        </Box>
        {customer.operations_account_external_id && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: { xs: 0, sm: '160px' } }}>
              Operations External ID:
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.85em', wordBreak: 'break-all' }}>
              {customer.operations_account_external_id}
            </Typography>
            <CopyIconButton text={customer.operations_account_external_id} iconSize={14} sx={{ p: 0.5, ml: 0.5 }} />
          </Box>
        )}
      </Box>
    }
    arrow
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: 3,
          border: '1px solid',
          borderColor: 'divider',
          maxWidth: viewportGutterMaxWidth,
        },
      },
    }}
  >
    <Typography
      variant="h6"
      noWrap
      component="div"
      sx={{ cursor: 'default' }}
    >
      {customer.name}
    </Typography>
  </TapTooltip>
);

const InvestigationHeader: React.FC<InvestigationHeaderProps> = ({
  selectedCustomer,
  connectionState,
  leftSidebarOpen,
  rightSidebarOpen,
  onToggleLeftSidebar,
  onToggleRightSidebar,
  onManageAccounts,
  onCustomerUpdated,
  onInvestigationCreated,
  onEffectiveLevelChange,
  inert = false,
}) => (
  <>
    <AppBar
      inert={inert}
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
      elevation={0}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="toggle left sidebar"
          aria-expanded={leftSidebarOpen}
          aria-controls={SHEET_ID.left}
          onClick={onToggleLeftSidebar}
          edge="start"
          sx={{ mr: 2 }}
        >
          {leftSidebarOpen ? <MenuOpenIcon /> : <MenuOpenIcon sx={{ transform: 'scaleX(-1)' }} />}
        </IconButton>

        <Box
          component="img"
          src="/cloud2_investigation_logo_transparent.png"
          alt="Cloud2 Investigation"
          sx={{ height: 40, mr: 2 }}
        />

        <Tooltip title={CONNECTION_TOOLTIP[connectionState]} arrow>
          <CircleIcon
            sx={{
              fontSize: 8,
              mr: 1,
              color: CONNECTION_COLOR[connectionState],
            }}
          />
        </Tooltip>

        <CustomerIdsTooltip customer={selectedCustomer} />

        <CustomerSwitcher selectedCustomer={selectedCustomer} />

        <Box sx={{ flexGrow: 1 }} />

        <EscalationControl customerId={selectedCustomer.id} onEffectiveLevelChange={onEffectiveLevelChange} />

        <UserMenu
          selectedCustomer={selectedCustomer}
          onManageAccounts={onManageAccounts}
          onCustomerUpdated={onCustomerUpdated}
          onInvestigationCreated={onInvestigationCreated}
        />

        <IconButton
          color="inherit"
          aria-label="toggle right sidebar"
          aria-expanded={rightSidebarOpen}
          aria-controls={SHEET_ID.right}
          onClick={onToggleRightSidebar}
          sx={{ mr: 1 }}
        >
          {rightSidebarOpen ? <MenuOpenIcon sx={{ transform: 'scaleX(-1)' }} /> : <MenuOpenIcon />}
        </IconButton>
      </Toolbar>
    </AppBar>

    <Toolbar />
  </>
);

export default InvestigationHeader;
